import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showToast } from "../../components/toastMessage/toastSlice";
import AddBottomButton from "../../components/Add/AddBottomButton";
import AddModel from "../../components/Add/AddModel";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import CustomRadioButton from "../../components/buttons/CustomRadioButton";
import ButtonWithImg from "../../components/buttons/ButtonWithImg";
import S3ImageUpload from "../../components/S3uploads/S3ImageUpload";
import useApi from "../../services/useApi";
import { setPreviousUrl } from "../../store/slices/geolocationSlice";
interface WalletTransferRecord {
  user_code: string;
  wallet_type: string;
  amount: number;
}

interface RechargeRecord {
  country_code: string;
  customer_phone_number: string;
  recharge_amount: number;
  customer_name: string;
  father_name: string;
  location: string;
  company_name: string;
  wallet_type: string;
}

interface ParsedCSVData {
  records: Array<WalletTransferRecord | RechargeRecord | Record<string, string | number>>;
}

const validationUrls = {
  recharge: 'merchant-recharge/bulk-mno',
  "wallet-transfer": "merchant-recharge/wallet/validate",
};
const columnHeaders = {
  "recharge": ["Country Code", "Customer Phone Number", "Recharge Amount", "Customer Name", "Father Name", "Location", "Company Name", "Wallet Type"],
  "wallet-transfer": ["User code", "Wallet type", "Amount"],
  "bundle-activation": ["Phone Number", "Bundle Type", "Amount"]
};
function Recharge() {
  const [searchParams, setSearchParams] = useSearchParams();
  const validOptions = ["recharge", "wallet-transfer", "bundle-activation"];
  const typeParam = searchParams.get("type");
  const initialOption = validOptions.includes(typeParam || "")
    ? typeParam!
    : "recharge";
  const [selectedOption, setSelectedOption] = useState(initialOption);

  useEffect(() => {
    if (!typeParam || !validOptions.includes(typeParam)) {
      setSearchParams({ type: "recharge" });
    }
  }, []);

  const handleOptionChange = (value: string) => {
    setSelectedOption(value);
    setSearchParams({ type: value });
    setUploadedFile("");
    setParsedData(null);
  };
  const [parsedData, setParsedData] = useState<ParsedCSVData | null>(null);
  const [uploadedFile, setUploadedFile] = useState("");
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState<Record<string, string>>({});
  const { PostAPI } = useApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();


  // Phone number validation helper function
  const validatePhoneNumber = (phone: string): { isValid: boolean; error?: string } => {
    if (!phone || phone.trim() === '') {
      return { isValid: false, error: 'Phone number is required' };
    }

    const trimmedPhone = phone.trim();

    if (/[a-zA-Z]/.test(trimmedPhone)) {
      return { isValid: false, error: 'Phone number can contain only digits' };
    }

    const cleanedPhone = trimmedPhone.replace(/[\s\-()]+/g, '');

    if (!/^\d+$/.test(cleanedPhone)) {
      return { isValid: false, error: 'Phone number contains invalid characters' };
    }

    if (cleanedPhone.length < 7 || cleanedPhone.length > 15) {
      return { isValid: false, error: 'Phone number must be between 7 and 15 digits' };
    }

    return { isValid: true };
  };

  // Wallet Type validation helper function
  const validateWalletType = (
    walletType: string
  ): { isValid: boolean; error?: string } => {
    if (!walletType || walletType.trim() === "") {
      return { isValid: false, error: "Wallet type is required" };
    }
    if (walletType === "Digital Wallet") {
      return {
        isValid: false,
        error: "Digital wallet payments are currently unavailable.",
      };
    }
    const trimmedType = walletType.trim();
    const validTypes = ["eTop Up", "Digital Wallet"];

    const isValid = validTypes.includes(trimmedType);

    if (!isValid) {
      return {
        isValid: false,
        error: 'Wallet type must be either "eTop Up" or "Digital Wallet"',
      };
    }

    return { isValid: true };
  };

    // Country code validation helper function
  const validateCountryCode = (countryCode: string): { isValid: boolean; error?: string } => {
    if (!countryCode || countryCode.trim() === '') {
      return { isValid: false, error: 'Country code is required' };
    }
    
    const trimmedCode = countryCode.trim();
    const codeWithoutPlus = trimmedCode.startsWith('+') ? trimmedCode.slice(1) : trimmedCode;
    
    if (!/^\d{1,4}$/.test(codeWithoutPlus)) {
      return { isValid: false, error: 'Country code must be 1-4 digits' };
    }
    
    return { isValid: true };
  };

  // Custom validation function for CSV records
  const customCSVValidation = (records: any[]): string | null => {
    for (let i = 0; i < records.length; i++) {
      const record = records[i];

      if (selectedOption === "wallet-transfer") {
        const walletTypeValidation = validateWalletType(record.wallet_type);
        if (!walletTypeValidation.isValid) {
          return `${walletTypeValidation.error}`;
        }
      }
      
      else if (selectedOption === 'recharge') {
        const countryCodeValidation = validateCountryCode(record.country_code);
        if (!countryCodeValidation.isValid) {
          return `${countryCodeValidation.error}`;
        }
        
        const phoneValidation = validatePhoneNumber(record.customer_phone_number);
        if (!phoneValidation.isValid) {
          return `${phoneValidation.error}`;
        }
        
        const amount = Number(record.recharge_amount);
        if (isNaN(amount) || amount <= 0) {
          return 'Recharge amount must be greater than 0';
        }

        const walletTypeValidation = validateWalletType(record.wallet_type);
        if (!walletTypeValidation.isValid) {
          return `${walletTypeValidation.error}`;
        }
      }
    }

    return null;
  };

  const handleFileUpload = (
    id: string,
    value: string,
    file?: File,
    csvData?: ParsedCSVData
  ) => {
    setUploadedFile(value);

    if (csvData) {
      setParsedData(csvData);
    }
  };
  const handleSubmit = async () => {
    const currentUrl = location.pathname + location.search;

    if (parsedData) {
      setLoading(true);
      try {
        const apiUrl =
          validationUrls[selectedOption as keyof typeof validationUrls];

        if (!apiUrl) {
          dispatch(
            showToast({
              type: "error",
              message: `${selectedOption} is not supported yet.`,
            })
          );
          setLoading(false);
          return;
        }

        let payload: unknown = parsedData;

        if (selectedOption === "recharge") {
          
          // Transform actual parsed data for recharge API
          const recharges = parsedData.records.map((record) => {
            const rechargeRecord = record as RechargeRecord;
            return {
              name: rechargeRecord.customer_name || "",
              country_code: rechargeRecord.country_code?.startsWith("+")
                ? rechargeRecord.country_code
                : `+${rechargeRecord.country_code || ""}`,
              phone_number: rechargeRecord.customer_phone_number || "",
              amount: rechargeRecord.recharge_amount || 1,
              wallet_type: rechargeRecord.wallet_type === "eTop Up" ? "etopup" : "digital",
              location: rechargeRecord.location || "",
              company_name: rechargeRecord.company_name || "",
              father_name: rechargeRecord.father_name || "",
            };
          });
          payload = { recharges };
        } else if (selectedOption === "wallet-transfer") {
          // Transform wallet transfer data - convert display values to API values
          const records = parsedData.records.map((record) => {
            const walletRecord = record as WalletTransferRecord;
            return {
              user_code: walletRecord.user_code || "",
              wallet_type: walletRecord.wallet_type === "eTop Up" ? "etopup" : "digital",
              amount: walletRecord.amount || 0,
            };
          });
          payload = { records };
        }
        // Future options can be handled here with additional else if conditions

        const response = await PostAPI(apiUrl, payload);

        if (response?.error === true) {
          dispatch(
            showToast({
              type: "error",
              message: "Submit failed. Please try again.",
            })
          );
          setLoading(false);
          return;
        }

        // const successMessage = `${selectedOption.charAt(0).toUpperCase() + selectedOption.slice(1).replace('-', ' ')} submitted successfully!`
        // For recharge the data exists in result
        const responseData =
          selectedOption === "recharge" ? response.data.results : response.data;

        // dispatch(showToast({ type: 'success', message: successMessage }))

        setTimeout(() => {
          setLoading(false);
          navigate(`/bulk-transfer/list-of-transfer?type=${selectedOption}`, {
            state: { responseData },
          });
        }, 1000);

        dispatch(setPreviousUrl(currentUrl));
      } catch (error) {
        console.error("Submit failed:", error);
        dispatch(
          showToast({
            type: "error",
            message: "Submit failed. Please try again.",
          })
        );
        setLoading(false);
      }
    } else {
      setFileError({ "csv-upload": "This field is required" });
    }
  };

  return (
    <div>
      {/* Breadcrumbs at the top */}
      <Breadcrumbs
        navPath={
          selectedOption === "recharge"
            ? ["Bulk Transfer", "Recharge"]
            : ["Bulk Transfer", "Wallet Transfer"]
        }
        heading="Bulk Transfer"
        noNavigation
        links={[`/bulk-transfer?type=${selectedOption}`]}
        hasAddPrivilege={true}
      />

      <AddModel>
        {/* Header section with radio buttons and download button */}
        <div className="flex items-center justify-between py-3 border-b border-[#E5E9EB]">
          <div>
            <CustomRadioButton
            textClasses="."
              options={[
                { id: "recharge", label: "Recharge"},
                { id: "wallet-transfer", label: "Wallet Transfer" },
                { id: "bundle-activation", label: "Bundle Activation" },
              ]}
              selected={selectedOption}
              padding="p-0"
              onChange={handleOptionChange}
              isDisabled={["bundle-activation"]}
            />
          </div>
          <div className="ml-4">
            <ButtonWithImg
              buttonName={"Download Sample CSV File"}
              ImgSrc={"download"}
              className="px-4 py-2"
              title="Download Sample CSV File"
              hideTextOnMobile={true} // Hide text on mobile/tablet to save space, show tooltip on hover
              buttonOnclick={() => {
                const headers =
                  columnHeaders[selectedOption as keyof typeof columnHeaders] ||
                  [];
                const dummyData = {
                  recharge: [
                    ["+93", "7028848577587", 100, "John Doe", "Robert Doe", "Karachi", "ABC Corp", "eTop Up"],
                    ["+93", "7128848577587", 200, "Jane Smith", "Michael Smith", "Lahore", "XYZ Ltd", "eTop Up"],
                  ],
                  "wallet-transfer": [
                    ["DST302119", "eTop Up", 100],
                    ["RET2454", "eTop Up", 200],
                    ["AGT1001", "eTop Up", 150],
                    ["SUB5678", "eTop Up", 300],
                    ["DST302120", "eTop Up", 250],
                    ["RET2455", "eTop Up", 180],
                    ["AGT1002", "eTop Up", 120],
                    ["SUB5679", "eTop Up", 350],
                    ["DST302121", "eTop Up", 220],
                    ["RET2456", "eTop Up", 280],
                  ],
                  "bundle-activation": [
                    ["1234567890", "Monthly Data", 500],
                    ["1234567891", "Weekly SMS", 100],
                    ["1234567892", "Daily Voice", 50],
                    ["1234567893", "Monthly Voice", 800],
                    ["1234567894", "Weekly Data", 200],
                    ["1234567895", "Daily SMS", 30],
                    ["1234567896", "Monthly SMS", 300],
                    ["1234567897", "Weekly Voice", 150],
                    ["1234567898", "Daily Data", 80],
                    ["1234567899", "Monthly All", 1000],
                  ],
                };

                const data =
                  dummyData[selectedOption as keyof typeof dummyData] || [];
                const csvContent = [
                  headers.join(","),
                  ...data.map((row) => row.join(",")),
                ].join("\n");
                const blob = new Blob([csvContent], { type: "text/csv" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${selectedOption}_template.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
                dispatch(
                  showToast({
                    type: "success",
                    message: "CSV template downloaded successfully",
                  })
                );
              }}
              testId={"download-csv"}
              disabledButton={false}
            />
          </div>
        </div>

        {/* Main content area with upload section */}
        <div className="flex flex-col xl:flex-row">
          {/* File upload section */}
          <div className="min-w-[25.625rem]">
            <S3ImageUpload
              heading={""}
              disabled={false}
              UploadPath={"bulk-transfer"}
              upload_id={"csv-upload"}
              handleUpload={handleFileUpload}
              uploadedFile={uploadedFile}
              setCustomError={setFileError}
              expectedColumns={
                columnHeaders[selectedOption as keyof typeof columnHeaders] ||
                []
              }
              customValidation={customCSVValidation}
            />
          </div>

          {/* Upload instructions */}
          <div className="xl:ml-[50px]">
            <h3 className="text-large font-semibold mb-1 mt-7 text-primary_text">
              Upload File
            </h3>

            <div className="text-body1 text-primary_text">
              <div className="flex items-start">
                <span className="text-xs font-bold mx-2 mt-0.5">•</span>
                <p className="text-primary_text font-medium">
                  Maximum 100 records allowed.
                </p>
              </div>

              <div className="flex items-start">
                <span className="text-xs font-bold mx-2 mt-0.5">•</span>
                <div>
                  <span className="text-primary_text font-medium">
                    The file must include the following columns in order:
                  </span>
                  <br />
                  <span className="text-primary_text">
                    {columnHeaders[
                      selectedOption as keyof typeof columnHeaders
                    ]?.join(", ") || "No columns defined for this option"}
                  </span>
                </div>
              </div>

              <div className="flex items-start">
                <span className="text-xs font-bold mx-2 mt-0.5">•</span>
                <p className="text-primary_text font-medium">
                  Make sure there are no extra or missing columns.
                </p>
              </div>
            </div>
          </div>
        </div>
      </AddModel>

      {/* Submit button at the bottom */}
      <div>
        <AddBottomButton
          buttonName="Save & Next"
          buttonWidth="w-[174px]"
          onClick={handleSubmit}
          loading={loading}
          testID="submit"
          disabled={loading || !uploadedFile}
        />
      </div>
    </div>
  );
}

export default Recharge;
